import { Popover } from '@rocketlane/ui';
import { formatTime } from '@vidstack/react';
import { SignalOccurrenceWithTimePeriod } from 'api/meeting/types';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useVideoPlayerContext } from './player-context';

type SignalNavigatorProps = {
  signalOccurrencesForMeetingWithTimePeriod: SignalOccurrenceWithTimePeriod[];
  triggerClassName?: string;
};
export const SignalNavigator = ({
  signalOccurrencesForMeetingWithTimePeriod,
  triggerClassName,
}: SignalNavigatorProps) => {
  const { currentTime, moveToTime: setCurrentTime } = useVideoPlayerContext();
  const transcriptRef = useRef(signalOccurrencesForMeetingWithTimePeriod);
  const [activeSignalOccurrence, setActiveSignalOccurrence] = useState<
    SignalOccurrenceWithTimePeriod | undefined
  >(undefined);

  useEffect(() => {
    transcriptRef.current = signalOccurrencesForMeetingWithTimePeriod;
  }, [signalOccurrencesForMeetingWithTimePeriod]);

  const debouncedScrollRef = useRef(
    debounce(
      (time: number) => {
        const segment = signalOccurrencesForMeetingWithTimePeriod.find(
          (signalOccurrence) => {
            const fromTime = parseFloat(signalOccurrence.fromTime);
            const toTime = parseFloat(signalOccurrence.toTime);

            return time >= fromTime && time <= toTime;
          },
        );

        if (segment?.fromTime !== activeSignalOccurrence?.fromTime) {
          setActiveSignalOccurrence(segment);
        }
      },
      300,
      { maxWait: 1000 },
    ),
  );

  useEffect(() => {
    debouncedScrollRef.current(currentTime);
  }, [currentTime]);

  useEffect(() => {
    const debouncedScroll = debouncedScrollRef.current;

    return () => {
      debouncedScroll.cancel();
    };
  }, []);

  return (
    <Popover
      triggerMode="hover"
      side="left"
      align="start"
      content={
        <div className="min-w-[252px] flex flex-col gap-03 p-04">
          <div className="body-short-sb">
            <FormattedMessage id="account.meeting_pages.signal_navigator.title" />
          </div>
          <div className="flex flex-col">
            {signalOccurrencesForMeetingWithTimePeriod.map(
              (signalOccurrence) => (
                <div
                  key={signalOccurrence.signalOccurrence.signalOccurrenceId}
                  className="flex items-center justify-between px-03 py-02 gap-03 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
                  onClick={() => {
                    setCurrentTime?.(parseInt(signalOccurrence.fromTime));
                  }}
                >
                  <div>{signalOccurrence.signalOccurrence.signalName}</div>
                  <div className="body-short-00 text-text-placeholder">
                    {formatTime(parseInt(signalOccurrence.fromTime))}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      }
    >
      <div className={`flex flex-col gap-04 max-h-full  ${triggerClassName}`}>
        {signalOccurrencesForMeetingWithTimePeriod.map((signalOccurrence) => (
          <div
            key={signalOccurrence.signalOccurrence.signalOccurrenceId}
            className={`w-[8px] h-[2px] cursor-pointer transition-all duration-200 ${activeSignalOccurrence?.fromTime === signalOccurrence.fromTime ? 'opacity-100 bg-[var(--button-primary)] w-[16px]' : 'opacity-50 bg-[var(--gray-30)] w-[8px]'}`}
          ></div>
        ))}
      </div>
    </Popover>
  );
};
